/**
 * FR12 fix #1 — Category-driven retry policy.
 *
 * Spec §L2650-2656. Hooked into TanStack `useQuery({ retry, retryDelay })`
 * via `applySdkDefaults`. Bench-marked against the v1 default-retry of 3
 * (which storms 5xx during a hiccup and floods Better Stack).
 *
 * Category ladders:
 *   network      · 250 / 500 / 1000 / 5000ms (cap), 4 retries
 *   auth         · refresh-once (1 retry — driven by auth-refresh middleware)
 *   validation   · NEVER retry (input was bad; server's response will be bad too)
 *   server       · 3 retries honoring Retry-After header when present
 *   rate-limited · 2 retries honoring retryAfterMs from the SdkError payload
 *   realtime-degraded · NEVER retry — NATS layer drives reconnect
 *   schema       · NEVER retry — payload shape changed; refetch won't fix it
 *   precondition_failed · NEVER retry
 *   invariant_violated · NEVER retry (programmer error)
 *
 * Non-SdkError throwables: retry once. Covers transient TypeErrors that
 * sometimes appear during HMR or hot-swap cycles.
 */

import type { SdkErrorCategory } from '../../types/SdkError.js';

/** Per-category retry budgets. 0 = never retry. */
export const RETRY_BUDGETS: Readonly<Record<SdkErrorCategory, number>> = Object.freeze({
  network: 4,
  auth: 1,
  server: 3,
  'rate-limited': 2,
  validation: 0,
  'realtime-degraded': 0,
  schema: 0,
  precondition_failed: 0,
  invariant_violated: 0,
});

/** Network-retry delay ladder (ms), capped at the last entry. */
export const NETWORK_RETRY_DELAYS_MS: readonly number[] = Object.freeze([250, 500, 1_000, 5_000]);

interface SdkErrorLike {
  readonly category?: unknown;
  readonly retryAfterMs?: unknown;
  readonly name?: unknown;
}

function asSdkError(err: unknown): SdkErrorLike | null {
  if (err === null || typeof err !== 'object') return null;
  const e = err as SdkErrorLike;
  if (e.name !== 'SdkError') return null;
  return e;
}

/**
 * The predicate TanStack `useQuery({ retry })` calls. Returns `true` to
 * retry, `false` to give up. `failureCount` starts at 1 for the first
 * failure (TanStack semantics).
 */
export function retryPolicy(failureCount: number, err: unknown): boolean {
  const sdkErr = asSdkError(err);
  if (sdkErr === null) {
    // Programmer-error throwables: one retry, then surface.
    return failureCount <= 1;
  }
  const cat = sdkErr.category as SdkErrorCategory | undefined;
  if (cat === undefined) return false;
  const budget = RETRY_BUDGETS[cat] ?? 0;
  return failureCount <= budget;
}

/**
 * The delay TanStack waits before issuing the next retry. Driven by
 * the failure's category — network uses the explicit ladder, server +
 * rate-limited honor the SdkError's `retryAfterMs` when present.
 */
export function retryDelayMs(failureCount: number, err: unknown): number {
  const sdkErr = asSdkError(err);
  if (sdkErr === null) return 500;
  const cat = sdkErr.category as SdkErrorCategory | undefined;
  if (cat === 'network') {
    const idx = Math.min(failureCount - 1, NETWORK_RETRY_DELAYS_MS.length - 1);
    return NETWORK_RETRY_DELAYS_MS[idx] ?? 5_000;
  }
  // server / rate-limited: honor retryAfterMs if SdkError supplies it.
  const ra = sdkErr.retryAfterMs;
  if (typeof ra === 'number' && ra > 0) return ra;
  // Server default: 1s.
  if (cat === 'server') return 1_000;
  // Rate-limited default: 5s (when no retryAfterMs hint).
  if (cat === 'rate-limited') return 5_000;
  // Auth + unknown: 0 (immediate; auth path waits on refresh).
  return 0;
}

/**
 * True iff the error is an SdkError whose category has a ZERO retry budget
 * (validation, schema, precondition_failed, invariant_violated,
 * realtime-degraded). Such a failure is deterministic — re-issuing the same
 * request returns the same error — so realtime catch-up refetches skip the
 * query entirely: with broadcast invalidation recipes (e.g. per-trade
 * subjects) an errored query would otherwise refetch on every event and
 * storm the backend.
 */
export function isNonRetryableSdkError(err: unknown): boolean {
  const sdkErr = asSdkError(err);
  if (sdkErr === null) return false;
  const cat = sdkErr.category as SdkErrorCategory | undefined;
  if (cat === undefined) return false;
  return (RETRY_BUDGETS[cat] ?? 0) === 0;
}
